/**
 * The platform operator's dashboard.
 *
 * Four questions, in the order an operator asks them: is work flowing, is any
 * provider sitting on it, is the concentration cap holding, and is anything
 * reaching the platform unattributed. The last one is the only figure on this
 * page that should always read zero, and it is shown rather than assumed:
 * an audit row nobody can attribute is a gap in the trail, and a trail with
 * gaps is not evidence.
 */
import type { Dict, Grant, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { Badge, Empty, Note, toneOf } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'
import { Bars, TileStrip } from './common'
import type { TileData } from './common'

export function PlatformDashboard({ data, section, grant }: {
  data: Dict; section: SectionMeta; grant?: Grant
}) {
  const { n, amount, dt, lbl, boot } = useSession()
  const throughput = (data.throughput || {}) as Dict
  const sla = (data.sla || []) as Dict[]
  const concentration = (data.concentration || []) as Dict[]
  const connectors = (data.connectors || []) as Dict[]
  const attribution = (data.attribution || []) as Dict[]
  const audit = (data.audit_volume || {}) as Dict
  const denials = (data.denials || {}) as Dict
  const cap = boot.constants.flow_cap

  return (
    <>
      <PageHead section={section} grant={grant} />
      <TileStrip tiles={(data.tiles || []) as TileData[]} />

      <Cols>
        <Panel title="Seven days of flow" explain="routing.runs">
          <Bars rows={[
            { k: 'routing runs', n: throughput.runs_7d },
            { k: 'applications routed', n: throughput.routed_7d },
            { k: 'decisions', n: throughput.decided_7d },
            { k: 'offers', n: throughput.offers_7d },
            { k: 'disbursements', n: throughput.disbursed_7d },
          ]} labelKey="k" valueKey="n" />
          <p className="xs muted">
            Counted at each stage rather than as a funnel percentage. A request
            can be routed to several providers, so the stages do not divide into
            one another and a conversion rate computed from them would be wrong.
          </p>
        </Panel>

        <Panel title="The audit trail itself" explain="audit.change_log">
          <Bars rows={[
            { k: 'changes recorded', n: audit.changes_7d },
            { k: 'distinct actors', n: audit.actors_7d },
            { k: 'break glass openings', n: audit.break_glass_7d },
            { k: 'unattributed', n: audit.unattributed_7d },
          ]} labelKey="k" valueKey="n" />
          {Number(audit.unattributed_7d) > 0 ? (
            <Note kind="bad">
              <b>{n(audit.unattributed_7d)} changes carry no actor.</b> Each one
              was captured, which is why it can be counted, and each one is a
              transaction that did not set its context. The attribution gap view
              lists them.
            </Note>
          ) : (
            <Note kind="good">
              <b>Every change in the period carries an actor.</b> Rows are
              written by triggers, so an unattributed row means a transaction
              opened without setting its context rather than a change that
              escaped the trail.
            </Note>
          )}
        </Panel>
      </Cols>

      <Panel title={`Provider concentration · cap ${n(cap, 0)}%`}
             explain="routing.concentration" tight>
        {concentration.length === 0 ? (
          <Empty title="No provider has taken a routable share this quarter" />
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Provider</th><th className="num">Routed</th>
                  <th className="num">Platform total</th><th className="num">Share</th>
                  <th>Against cap</th>
                </tr>
              </thead>
              <tbody>
                {concentration.map((r) => (
                  <tr key={String(r.provider_name)}>
                    <td data-label="Provider">{r.provider_name as string}</td>
                    <td data-label="Routed" className="num">{n(r.routed_count)}</td>
                    <td data-label="Platform total" className="num">{n(r.platform_total)}</td>
                    <td data-label="Share" className="num">{n(r.share_pct, 1)}%</td>
                    <td data-label="Against cap">
                      <Badge tone={r.cap_breached ? 'bad' : 'good'}>
                        {r.cap_breached ? 'over the cap' : 'within the cap'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="xs muted">
          The cap applies only once a quarter carries at least{' '}
          {n(boot.constants.flow_cap_min_volume)} applications. Below that a
          single request moves a share by tens of points, and capping on noise
          would withhold routing from the provider best placed to approve.
        </p>
      </Panel>

      <Panel title="Where work is waiting" explain="application.queue" tight>
        {sla.length === 0 ? <Empty title="No provider has open work" /> : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Provider</th><th className="num">Open</th>
                  <th className="num">Breached</th><th className="num">Median hours</th>
                </tr>
              </thead>
              <tbody>
                {sla.map((r) => (
                  <tr key={String(r.provider_id)}>
                    <td data-label="Provider">{r.provider_name as string}</td>
                    <td data-label="Open" className="num">{n(r.open)}</td>
                    <td data-label="Breached" className="num">
                      {Number(r.breached) > 0
                        ? <Badge tone="bad">{n(r.breached)}</Badge>
                        : n(0)}
                    </td>
                    <td data-label="Median hours" className="num">{n(r.median_hours, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Integrations" explain="connector.registry" tight>
        {connectors.length === 0 ? <Empty title="No connector is registered" /> : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Connector</th><th>System</th><th>Direction</th>
                  <th>Environment</th><th>Breaker</th>
                  <th className="num">Calls 24h</th><th className="num">Success</th>
                  <th className="num">p99</th><th>Last call</th>
                </tr>
              </thead>
              <tbody>
                {connectors.map((c) => (
                  <tr key={String(c.connector_code)}>
                    <td data-label="Connector">
                      <span className="mono clip">{c.connector_code as string}</span>
                    </td>
                    <td data-label="System">{c.system_code as string}</td>
                    <td data-label="Direction">
                      <Badge tone="muted">{lbl(c.direction)}</Badge>
                    </td>
                    <td data-label="Environment">
                      <Badge tone={toneOf(c.environment)}>{lbl(c.environment)}</Badge>
                    </td>
                    <td data-label="Breaker">
                      <Badge tone={c.breaker_state === 'closed' ? 'good'
                        : c.breaker_state === 'half_open' ? 'warn' : 'bad'}>
                        {lbl(c.breaker_state)}
                      </Badge>
                    </td>
                    <td data-label="Calls 24h" className="num">{n(c.calls_24h)}</td>
                    <td data-label="Success" className="num">
                      {c.success_pct === null ? '—' : `${n(c.success_pct, 1)}%`}
                    </td>
                    <td data-label="p99" className="num">
                      {c.p99_ms === null ? '—' : `${n(c.p99_ms)} ms`}
                    </td>
                    <td data-label="Last call">
                      {c.last_call_at ? dt(c.last_call_at) : <span className="muted">never</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="xs muted">
          A connector that has never been called shows no success rate rather
          than a hundred percent. Ten of the sixteen national integrations read
          not started, and saying so is the point: a dashboard that showed them
          green would be describing a sandbox.
        </p>
      </Panel>

      <Cols>
        <Panel title="Disclosure decisions" explain="consent.access_log">
          <Bars rows={[
            { k: 'accesses', n: denials.total },
            { k: 'refused by the gateway', n: denials.denied },
          ]} labelKey="k" valueKey="n" />
          <p className="xs muted">
            Refusals are logged alongside the disclosures. A register that
            recorded only successful accesses could not answer whether the
            gateway ever said no.
          </p>
        </Panel>

        <Panel title="Attribution gaps" explain="audit.attribution_gap" tight>
          {attribution.length === 0 ? (
            <Note kind="good">
              <b>No gap.</b> Every captured change names the person, the role and
              the session behind it.
            </Note>
          ) : (
            <div className="tbl-wrap">
              <table className="dl">
                <thead><tr><th>Table</th><th className="num">Rows</th></tr></thead>
                <tbody>
                  {attribution.map((r, i) => (
                    <tr key={i}>
                      <td data-label="Table">
                        <span className="mono">{String(r.table_name || r.table_schema)}</span>
                      </td>
                      <td data-label="Rows" className="num">{n(r.n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </Cols>

      <Panel title="What this dashboard will not tell you" explain={section.section_key}>
        <p className="muted">
          There is no approval rate here, and no figure describing how likely a
          provider is to say yes. Published league tables of that kind change
          provider behaviour towards the measure rather than towards the
          applicant, and the routing weights are published precisely so that
          nobody has to infer them from outcomes. Total cost of finance is
          computed by method {boot.constants.tcf_version}, and fee level is
          absent from the routing weights by design: approval fit{' '}
          {n(boot.constants.weights.approval_fit * 100)}%, speed{' '}
          {n(boot.constants.weights.speed * 100)}%, product fit{' '}
          {n(boot.constants.weights.product_fit * 100)}%, stated preference{' '}
          {n(boot.constants.weights.preference * 100)}%.
        </p>
        <p className="xs muted">
          Figures on this page are counts and medians over the stated window, not
          projections. {amount(0)} appears where a currency figure is genuinely
          zero rather than missing.
        </p>
      </Panel>
    </>
  )
}
